import { useTranslation, Translation } from 'react-i18next';
import React, { memo, useEffect, useRef, useMemo, useCallback, useState, ReactNode } from 'react';
import cx, { ClassValue } from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';

import {
  IconRcFeedReply,
  IconRcFeedCredibility,
  // IconRcFeedRealized,
  IconRcFeedRepost,
  IconRcFeedBookmark,
  IconRcFeedBookmarked,
  IconRcFeedLike,
  IconRcFeedLiked,
  IconRcFeedRead,
  IconRcStreamQuote,
  IconRcStreamQuoteList,
} from '@/assets/stream';
import { HelperTooltip, Modal, RichTextView, Popup } from '@/components';
import { suicideToast } from '@/components/SystemErrorToast';
import { useSwitch, useCurrentAddressInfo, useDeviceTypeIsMobile, useQueryParams } from '@/hooks';
import { formatNetworth, formatNum, formatStreamUsd, login, openInDebank } from '@/utils';

import { FeedArticleDetail, FeedArticle } from '../../types';
import {
  useArticleTrust,
  useArticleRead,
  useArticleRepost,
  useArticleRepostDelete,
  useArticleBookmark,
} from '../../hooks';
import { formatStreamValue } from '../../utils';
import { OperationUserListModal, StreamOperationType } from '../OperationUserListModal';
import { ArticleCreateCommentModal } from '../ArticleCreateCommentModal';
import { openCreateQuoteModal } from '../CreateQuoteModal';
import { SharedByGroupMemberTag } from '../SharedByGroupMemberTag';
import { ThreadContent, Thread_Article_Types } from './ThreadContent';
import { PayQaContent } from './PayQaContent';
import { TagList } from '../TagList';
import { PrimePostVoteBtn } from '../PrimePostVoteBtn';
import style from './index.module.scss';
import { QuotedArticle } from './QuotedArticle';

type ArticleContentProps = {
  className?: string;
  articleDetail: FeedArticleDetail;
  onClick?: () => void;
  maxDisplay?: number;
  showRawImage?: boolean;
  source?: 'timeline' | 'detail';
  maxImgSize?: [number, number];
  contentClassName?: string;
  enableReadFeedback?: boolean;
  enableRefCode?: boolean;
  enableContributorId?: boolean;
  enableTranslate?: boolean;
  sharedByGroupMember?: UnionAccount;
  disableComment?: boolean;
  onPromotionClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  showVoteBtn?: boolean;
};

const { CommonModal } = Modal;

const OPERACTION_ALIGN = {
  targetOffset: [6, -4] as [number, number],
};

const stopPropagation = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
};

export const ArticleContent = memo((props: ArticleContentProps) => {
  const {
    className,
    articleDetail,
    onClick,
    maxDisplay,
    showRawImage,
    source = 'timeline',
    maxImgSize,
    sharedByGroupMember,
    contentClassName,
    enableReadFeedback,
    enableRefCode,
    enableContributorId,
    enableTranslate,
    disableComment,
    onPromotionClick,
    showVoteBtn,
  } = props;
  const { t } = useTranslation();
  const [refCode] = useQueryParams<string | null>('r', undefined);
  const history = useHistory();

  const { isVerified, userInfo, address } = useCurrentAddressInfo();
  const isMobile = useDeviceTypeIsMobile();

  const isRankBelow1k = useMemo(() => {
    return isVerified && userInfo?.rank_at && userInfo?.rank_at <= 1000;
  }, [isVerified, userInfo?.rank_at]);

  const ele = useRef(null);
  const isRead = useRef(false);
  const { on, turnOn, turnOff } = useSwitch();
  const { on: realizedShown, turnOn: showRealized, turnOff: hideRealized } = useSwitch();
  const {
    on: onOperationUserListModal,
    turnOn: showOperationUserListModal,
    turnOff: hideOperationUserListModal,
  } = useSwitch();
  const [operationUserListModalType, setOperationUserListModalType] =
    useState<StreamOperationType>('repost');
  const { article } = articleDetail;
  const isDetails = useMemo(() => source === 'detail', [source]);
  const { mutate: readArticle } = useArticleRead();
  const { mutate: trustArticle, isLoading: isTrusting } = useArticleTrust(
    article.id,
    article.is_trust,
  );
  const { mutate: repostArticle, isLoading: isReposting } = useArticleRepost(article.id);
  const { mutate: deleteRepostArticle, isLoading: isRepostDeleting } = useArticleRepostDelete(
    article.id,
  );
  const { mutate: updateBookmark, isLoading: isBookmarking } = useArticleBookmark(
    articleDetail.article.id,
    !article.is_bookmarked,
  );

  // eslint-disable-next-line
  const showOperationUserListModalWithType = useCallback(
    (type: StreamOperationType) => {
      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      setOperationUserListModalType(type);
      showOperationUserListModal();
    },
    [isMobile, showOperationUserListModal, article.id],
  );

  const needPaid = useMemo(() => {
    const payThread =
      article.type === 'pay_thread' && !article.is_paid && article.creator_id !== address;

    const payQa =
      article.type === 'pay_qa' &&
      article.pay_qa?.answerer?.id !== address &&
      article.creator_id !== address &&
      !article.pay_qa?.is_paid;

    return !!address && (payThread || payQa);
  }, [article, address]);

  const disableOp = useMemo(
    () => article.is_withdrawn || needPaid,
    [article.is_withdrawn, needPaid],
  );

  const disableTrustOp = useMemo(
    () => article.is_withdrawn && !isRankBelow1k,
    [article.is_withdrawn, isRankBelow1k],
  );

  const commentTip = useMemo(() => {
    if (disableComment) {
      return t('ArticleContent.disabledComment');
    }
    if (disableOp) {
      return needPaid ? t('ArticleContent.paidTip') : null;
    }
    return t('ArticleContent.addComment');
  }, [disableComment, disableOp, needPaid, t]);

  const observer = useRef(
    new IntersectionObserver(
      ([entry]) => {
        if (
          isVerified &&
          !isRead.current &&
          !article?.is_read &&
          entry.intersectionRatio &&
          enableReadFeedback
        ) {
          const ref_code = enableRefCode && Number(refCode) ? Number(refCode) : undefined;

          readArticle({
            id: article.id,
            source,
            contributor_id: enableContributorId ? article._contributorId : undefined,
            ref_code,
          });

          // quote 的文章也发
          if (article.type === 'quote' && article.quote_article) {
            readArticle({
              id: article.quote_article.id,
              source,
              contributor_id: article.creator_id,
              ref_code,
            });
          }
          isRead.current = true;
        }
      },
      {
        root: null,
        rootMargin: '300px',
        threshold: 0.3,
      },
    ),
  );

  useEffect(() => {
    const _ele = ele.current;
    const currentObserver = observer.current;

    if (_ele) {
      currentObserver.observe(_ele);
    }

    return () => {
      if (_ele) {
        currentObserver.unobserve(_ele);
        currentObserver.disconnect();
      }
    };
  }, []);

  const handleReplyClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (disableOp || disableComment) {
        return;
      }
      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }
      turnOn();
    },
    [disableOp, isMobile, isVerified, article.id, turnOn, disableComment],
  );

  const handleBookmarkClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (isBookmarking) {
        return;
      }

      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }

      updateBookmark();
    },
    [isMobile, isVerified, article.id, updateBookmark, isBookmarking],
  );

  const [isTrust] = useMemo(
    () => [
      articleDetail.article.is_trust && isVerified,
      articleDetail.article.is_trust === false && isVerified,
    ],
    [articleDetail.article.is_trust, isVerified],
  );

  // const handleDistrustClick = useCallback(
  //   (e: React.MouseEvent<HTMLDivElement>) => {
  //     e.stopPropagation();

  //     if (disableTrustOp || isTrusting) {
  //       return;
  //     }

  //     if (isMobile) {
  //       openInDebank({ link: `/stream/${article.id}` });
  //       return;
  //     }

  //     if (!isVerified) {
  //       login();
  //       return;
  //     }

  //     trustArticle(isDistrust ? null : false);
  //   },
  //   [disableTrustOp, isTrusting, isMobile, article.id, isVerified, trustArticle, isDistrust],
  // );

  const handleTrustClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (disableTrustOp || isTrusting) {
        return;
      }
      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }

      trustArticle(isTrust ? null : true);
    },
    [disableTrustOp, isTrusting, isMobile, isVerified, trustArticle, isTrust, article.id],
  );

  const handleRealizedClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      showRealized();
    },
    [showRealized],
  );

  const handleRepostClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (isReposting || isRepostDeleting || disableOp) {
        return;
      }
      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }
      if (userInfo?.is_suicide) {
        suicideToast();
        return;
      }

      if (articleDetail.article.is_reposted) {
        deleteRepostArticle();
      } else {
        repostArticle();
      }
    },
    [
      isReposting,
      isRepostDeleting,
      disableOp,
      isMobile,
      isVerified,
      articleDetail.article.is_reposted,
      article.id,
      deleteRepostArticle,
      repostArticle,
      userInfo?.is_suicide,
    ],
  );

  const iconSize = isDetails ? 20 : 16;

  const ReplyBtn = (
    <OperationBtn
      isMobile={isMobile}
      isDetail={isDetails}
      title={commentTip}
      disabled={disableComment || disableOp}
      onClick={handleReplyClick}
      Icon={<IconRcFeedReply width={iconSize} height={iconSize} />}>
      {article?._commentCount}
    </OperationBtn>
  );

  const RepostBtn = (
    <Popup
      on="click"
      arrow={false}
      className="repost-popup"
      offsetX={-8}
      position="bottom left"
      disabled={disableOp}
      trigger={
        <div className={style.operation}>
          <OperationBtn
            isMobile={isMobile}
            isDetail={isDetails}
            title={
              disableOp
                ? needPaid
                  ? t('ArticleContent.paidRepost')
                  : null
                : articleDetail.article.is_reposted
                  ? t('ArticleContent.cancel-repost')
                  : t('ArticleContent.repost')
            }
            disabled={disableOp}
            className={articleDetail.article.is_reposted && style.isRepost}
            Icon={
              <IconRcFeedRepost
                color={articleDetail.article.is_reposted ? 'var(--color-primary)' : ''}
                width={iconSize}
                height={iconSize}
              />
            }>
            {article?._repostCount}
          </OperationBtn>
        </div>
      }>
      {(onClosePopup: () => void) => (
        <>
          <div
            className={style.popupItem}
            onClick={e => {
              onClosePopup();
              handleRepostClick(e);
            }}>
            <IconRcFeedRepost className={style.popupItemIcon} />
            {articleDetail.article.is_reposted
              ? t('ArticleContent.cancel-repost')
              : t('ArticleContent.repost')}
          </div>
          <div
            className={style.popupItem}
            onClick={() => {
              onClosePopup();
              openCreateQuoteModal({
                quoteArticle: article,
              });
            }}>
            <IconRcStreamQuote className={style.popupItemIcon} />
            <Translation>{t => t('ArticleContent.1aoeos')}</Translation>
          </div>
          {article?.quote_count && isDetails ? (
            <div
              className={style.popupItem}
              onClick={e => {
                onClosePopup();
                handleQuoteViewClick(e);
              }}>
              <IconRcStreamQuoteList className={style.popupItemIcon} />
              <Translation>{t => t('ArticleContent.ck58pe')}</Translation>
            </div>
          ) : null}
        </>
      )}
    </Popup>
  );

  const handleQuoteViewClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      history.push(`/stream/${article?.id!}/quotes`);
    },
    [history, article?.id],
  );

  const TrustBtn = (
    <OperationBtn
      isMobile={isMobile}
      isDetail={isDetails}
      title={
        disableTrustOp
          ? null
          : isTrust
            ? t('ArticleContent.cancelTrust')
            : t('ArticleContent.trust')
      }
      disabled={disableTrustOp}
      className={isTrust && style.isTrust}
      onClick={handleTrustClick}
      Icon={
        isTrust ? (
          <IconRcFeedLiked width={iconSize} height={iconSize} />
        ) : (
          <IconRcFeedLike width={iconSize} height={iconSize} />
        )
      }>
      {article._trustCount}
    </OperationBtn>
  );

  const BookmarkBtn = (
    <OperationBtn
      isMobile={isMobile}
      isDetail={isDetails}
      title={
        article.is_bookmarked ? t('FeedMoreAction.cancelBookmark') : t('FeedMoreAction.addBookmark')
      }
      className={article.is_bookmarked && style.isBookmarked}
      onClick={handleBookmarkClick}
      Icon={
        article.is_bookmarked ? (
          <IconRcFeedBookmarked width={iconSize} height={iconSize} />
        ) : (
          <IconRcFeedBookmark width={iconSize} height={iconSize} />
        )
      }>
      {isDetails ? article._bookmarkCount : null}
    </OperationBtn>
  );

  return (
    <>
      <div className={cx(style.articleMain, isMobile && style.mobile, className)} ref={ele}>
        {Thread_Article_Types.includes(article.type) && article.thread_count ? (
          <ThreadContent
            article={articleDetail.article}
            maxDisplay={maxDisplay}
            showRawImage={showRawImage}
            maxImgSize={maxImgSize}
            enableTranslate={enableTranslate}
            isDetails={isDetails}
            disableOp={disableOp}
            showVoteBtn={showVoteBtn}
          />
        ) : article.type === 'pay_qa' ? (
          <PayQaContent
            article={articleDetail.article}
            maxDisplay={maxDisplay}
            showRawImage={showRawImage}
            maxImgSize={maxImgSize}
            enableTranslate={enableTranslate}
            isDetails={isDetails}
            disableOp={disableOp}
          />
        ) : (
          <>
            <RichTextView
              article={articleDetail.article}
              onClick={onClick}
              maxDisplay={maxDisplay}
              showRawImage={showRawImage}
              maxImgSize={maxImgSize}
              className={contentClassName}
              enableTranslate={enableTranslate}
              isDetails={isDetails}
            />
            <TagList article={article} />
          </>
        )}
        {article.quote_article ? (
          <QuotedArticle
            article={article.quote_article}
            maxDisplay={maxDisplay}
            showRawImage={showRawImage}
            maxImgSize={maxImgSize}
            className={contentClassName}
            enableTranslate={enableTranslate}
            isDetails={isDetails}
            onlyOriginImg={!!article.quote_article?.images?.length && !article.images?.length}
          />
        ) : null}
        {sharedByGroupMember ? <SharedByGroupMemberTag user={sharedByGroupMember} /> : null}

        {isDetails && articleDetail?.article?.prime_post_issue ? (
          <PrimePostVoteBtn feed={articleDetail} isDetail />
        ) : null}

        {isDetails ? (
          <div>
            {!isMobile ? (
              <div className={cx(style.operations, style.detailOperations)}>
                <div className={style.leftOps}>
                  <StreamStats
                    value={
                      article.realized_value ? `$${formatStreamValue(article.realized_value)}` : ''
                    }
                    title={t('ArticleContent.1taidjz')}
                    onClick={handleRealizedClick}
                  />
                  {/* <StreamStats
                    value={article._repostCount}
                    title={t('ArticleContent.repost')}
                    onClick={() => showOperationUserListModalWithType('repost')}
                  /> */}
                  <StreamStats value={article._readCount} title={t('ArticleContent.read')} />
                  {article.reward_usd_value ? (
                    <div className={style.depositValue} onClick={onPromotionClick}>
                      {article._rewardUsdValue} {t('ArticleContent.promotion')}
                    </div>
                  ) : null}
                </div>
                <div className={style.rightOps}>
                  {/* <StreamStats
                    value={article._trustCount}
                    title={t('ArticleContent.trust_stats')}
                    onClick={() => showOperationUserListModalWithType('trust')}
                  /> */}
                  {/* <StreamStats
                    value={article._distrustCount}
                    title={t('ArticleContent.distrust')}
                    onClick={() => showOperationUserListModalWithType('distrust')}
                  /> */}
                </div>
              </div>
            ) : (
              <div className={style.mobileOperations}>
                <div className={style.topOps}>
                  <div className={style.flexSection}>
                    <StreamStats
                      value={formatNetworth(article.realized_value)}
                      title={t('ArticleContent.1taidjz')}
                      onClick={handleRealizedClick}
                    />
                    <StreamStats value={article._readCount} title={t('ArticleContent.read')} />
                  </div>
                  {article.reward_usd_value ? (
                    <div className={style.depositValue}>
                      {article._rewardUsdValue} {t('ArticleContent.promotion')}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
            <div
              className={cx(style.operations, style.footerOperations, isMobile && style.isMobile)}>
              {ReplyBtn}
              {RepostBtn}
              {TrustBtn}
              {BookmarkBtn}
              {/* <HelperTooltip
                disable={isMobile}
                title={
                  disableTrustOp
                    ? needPaid
                      ? t('ArticleContent.paidDistrust')
                      : null
                    : isDistrust
                      ? t('ArticleContent.cancelDistrust')
                      : t('ArticleContent.distrust')
                }
                placement="topLeft"
                align={OPERACTION_ALIGN}>
                <div
                  className={cx(
                    style.operation,
                    isDistrust && style.isDistrust,
                    disableTrustOp && style.isDisable,
                  )}
                  onClick={handleDistrustClick}>
                  <div className={cx(style.opIconWrap, style.bigSizeOpIconWrap)}>
                    <IconRcFeedDistrust
                      color={isDistrust ? 'var(--color-red)' : ''}
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
              </HelperTooltip> */}
            </div>
          </div>
        ) : (
          <div className={style.operations}>
            <div className={style.leftOps}>
              {ReplyBtn}
              {RepostBtn}
              {TrustBtn}
              <HelperTooltip
                disable={isMobile}
                title={t('ArticleContent.read')}
                placement="topLeft"
                align={OPERACTION_ALIGN}>
                <div className={cx(style.operation, style.unclickable)} onClick={stopPropagation}>
                  <div className={cx(style.opIconWrap, isMobile && style.isMobile)}>
                    <IconRcFeedRead />
                  </div>
                  {article._readCount}
                </div>
              </HelperTooltip>
              {/* <HelperTooltip
                disable={isMobile}
                title={t('ArticleContent.realizedValue')}
                placement="topLeft"
                align={OPERACTION_ALIGN}>
                <div
                  className={cx(style.operation, isMobile && style.isMobile)}
                  onClick={handleRealizedClick}>
                  <div className={cx(style.opIconWrap, isMobile && style.isMobile)}>
                    <IconRcFeedRealized />
                  </div>
                  {article._realizedValue}
                </div>
              </HelperTooltip> */}
            </div>
            <div className={style.rightOps}>
              {article.reward_usd_value ? (
                <div
                  className={cx(style.depositValue, isMobile && style.isMobile)}
                  onClick={onPromotionClick}>
                  {article._rewardUsdValue} {isMobile ? '' : t('ArticleContent.promotion')}
                </div>
              ) : null}
              {BookmarkBtn}

              {/* {isMobile ? null : (
                <HelperTooltip
                  disable={isMobile}
                  title={
                    disableTrustOp
                      ? needPaid
                        ? t('ArticleContent.paidDistrust')
                        : null
                      : isDistrust
                        ? t('ArticleContent.cancelDistrust')
                        : t('ArticleContent.distrust')
                  }
                  placement="topLeft"
                  align={OPERACTION_ALIGN}>
                  <div
                    className={cx(
                      style.operation,
                      isDistrust && style.isDistrust,
                      isMobile && style.isMobile,
                      disableTrustOp && style.isDisable,
                    )}
                    onClick={handleDistrustClick}>
                    <div className={cx(style.opIconWrap, isMobile && style.isMobile)}>
                      <IconRcFeedDistrust color={isDistrust ? 'var(--color-red)' : ''} />
                    </div>
                    {article._distrustCount}
                  </div>
                </HelperTooltip>
              )} */}
            </div>
          </div>
        )}
      </div>
      {on ? (
        <ArticleCreateCommentModal
          on={on}
          articleId={article.id}
          turnOff={turnOff}
          contributorId={enableContributorId ? article._contributorId : undefined}
        />
      ) : null}
      {realizedShown ? <RealizedValueModal article={article} turnOff={hideRealized} /> : null}

      {onOperationUserListModal ? (
        <OperationUserListModal
          articleId={article.id}
          modalType={operationUserListModalType}
          turnOff={hideOperationUserListModal}
        />
      ) : null}
    </>
  );
});

const OperationBtn = (props: {
  disabled?: boolean;
  isMobile?: boolean;
  isDetail?: boolean;
  title: string | null;
  className?: ClassValue;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  Icon: ReactNode;
  children?: ReactNode;
}) => {
  const { isDetail, disabled, title, className, onClick, Icon, children, isMobile } = props;

  return (
    <HelperTooltip disable={isMobile} title={title} placement="topLeft" align={OPERACTION_ALIGN}>
      <div
        className={cx(
          style.operation,
          className,
          isMobile && !isDetail && style.isMobile,
          disabled && style.isDisable,
        )}
        onClick={onClick}>
        <div
          className={cx(
            style.opIconWrap,
            isMobile && !isDetail ? style.isMobile : isDetail && style.bigSizeOpIconWrap,
          )}>
          {Icon}
        </div>
        {children}
      </div>
    </HelperTooltip>
  );
};

const StreamStats = memo(
  ({
    title,
    value,
    onClick,
    className,
  }: {
    title: string;
    className?: string;
    value?: number | string;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  }) => {
    if (!value || value === '0') {
      return null;
    }
    return (
      <div
        className={cx(style.streamStats, onClick && style.clickable, className)}
        onClick={onClick}>
        <span className={style.streamStatsValue}>{value}</span>
        <span className={style.streamStatsTitle}>{title}</span>
      </div>
    );
  },
);

export const ImageSkeleton = memo(() => {
  return <Skeleton width="100%" height={260} borderRadius={12} />;
});

export const useRealizedValues = (article?: FeedArticle) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      { label: t('ArticleContent.19ec8hc'), value: formatNum(article?.read_count, 0) },
      {
        label: t('ArticleContent.1de7uxe'),
        value: article ? formatStreamUsd(article?.tvr) || t('ArticleContent.tobe') : '-',
      },
      {
        label: t('ArticleContent.1cefz9p'),
        value: article ? formatStreamUsd(article?.vr_median) || t('ArticleContent.tobe') : '-',
      },
      {
        label: t('ArticleContent.1ndem5u'),
        value: article ? formatStreamUsd(article?.vr_avg) || t('ArticleContent.tobe') : '-',
      },
    ],
    [article, t],
  );
};

const RealizedValueModal = ({
  article,
  turnOff,
}: {
  article: FeedArticle;
  turnOff: () => void;
}) => {
  const { t } = useTranslation();

  const realizedValue = useMemo(() => formatNetworth(article.realized_value), [article]);
  const isMobile = useDeviceTypeIsMobile();
  const relatedFields = useRealizedValues(article);

  return (
    <CommonModal
      containerClassName={cx(style.realizedModal, isMobile && style.isMobile)}
      turnOff={turnOff}>
      <div className={style.realizedTitle}>{t('ArticleContent.1taidjz')}</div>
      <div className={style.realizedValue}>{realizedValue || t('ArticleContent.tobe')}</div>
      <div className={style.realizedDesc}>{t('ArticleContent.1r170j0')}</div>
      <div className={style.relatedFields}>
        {relatedFields?.map(x => (
          <div className={style.field}>
            <span className={style.fieldLabel}>{x.label}</span>
            <span className={style.fieldValue}>{x.value}</span>
          </div>
        ))}
      </div>
    </CommonModal>
  );
};

export const ArticleCredibility = ({
  article,
  isDetails,
}: {
  article: FeedArticle;
  isDetails?: boolean;
}) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const trustDegree = article.trust_degree;
  const modalTitle = useMemo(() => {
    if (isMobile) return `${t('ArticleContent.contentCredibility')}: ${trustDegree}`;
    return t('ArticleContent.contentCredibility');
  }, [isMobile, trustDegree, t]);
  const handleCredibilityClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      Modal.info({
        title: modalTitle,
        content: (
          <>
            <div className={style.credibilityLine}>{t('ArticleContent.lswdgd')}</div>
          </>
        ),
        closeable: true,
        hideConfirmBtn: true,
      });
    },
    [modalTitle, t],
  );

  const color = useMemo(() => {
    return article.trust_degree > 1 ? 'var(--color-mid-blue-title)' : 'var(--color-red)';
  }, [article.trust_degree]);

  const width = useMemo(() => {
    return {
      width: `${(article.trust_degree / 5) * 100}%`,
    };
  }, [article.trust_degree]);

  return (
    <div
      className={cx(style.articleCredibility, isMobile && style.isMobile)}
      onClick={handleCredibilityClick}>
      <HelperTooltip
        disable={isMobile}
        title={t('ArticleContent.credTip')}
        placement="top"
        align={OPERACTION_ALIGN}>
        <div className={style.operation}>
          <div className={cx(style.opIconWrap, isMobile && style.isMobile, style.isCredibility)}>
            <div className={style.credibilityIcon}>
              <IconRcFeedCredibility color="var(--select-divider)" />
              <div className={style.credibilityAbove} style={width}>
                <IconRcFeedCredibility color={color} />
              </div>
            </div>
          </div>
          <span className={cx(!isDetails && style.simpleDegree)}>{article.trust_degree}</span>
        </div>
      </HelperTooltip>
    </div>
  );
};
